import React from 'react';
import { Badge } from '@mantine/core';
import { StatusBadgeProps } from './StatusBadge.types';
import { getReviewStatusColor } from '../../../../domain/value-objects/ReviewStatus';
import { MatchStatus } from '../../../../domain/value-objects/MatchStatus';
import { ReviewStatus } from '../../../../domain/value-objects/ReviewStatus';

/**
 * StatusBadge Component
 *
 * SESSION_045: Updated to prioritize ReviewStatus (manual admin review workflow) over MatchStatus (algorithm assessment)
 *
 * Displays a color-coded status badge for mention review status
 *
 * Review Status Color scheme (Primary):
 * - Pending: Orange - Awaiting admin review
 * - Approved: Green - Admin confirmed authentic match
 * - Rejected: Gray - Admin rejected as false positive
 * - Flagged: Yellow - Admin flagged for investigation
 *
 * Match Status Color scheme (Legacy/Algorithm info):
 * - Unmatched: Orange - No business match found
 * - Matched: Blue - Algorithmically matched to business
 * - Verified: Green - Algorithm verified match
 * - Rejected: Gray - Algorithm rejected match
 *
 * Usage:
 * ```tsx
 * <StatusBadge status="Pending" />
 * <StatusBadge status="Approved" variant="filled" />
 * <StatusBadge matchStatus="Matched" /> // Legacy use
 * ```
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  matchStatus,
  size = 'md',
  variant = 'light'
}): React.ReactElement => {
  // SESSION_045: Prefer ReviewStatus over MatchStatus
  const displayStatus: string = status ?? matchStatus ?? 'Pending';

  // Map match status to color (legacy algorithm assessment)
  const getMatchStatusColor = (statusValue: MatchStatus): string => {
    switch (statusValue) {
      case 'Unmatched':
        return 'orange';
      case 'Matched':
        return 'blue';
      case 'Verified':
        return 'green';
      case 'Rejected':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Determine color based on which status is provided
  const color = status
    ? getReviewStatusColor(status as ReviewStatus)
    : getMatchStatusColor(matchStatus as MatchStatus);

  return (
    <Badge color={color} variant={variant} size={size}>
      {displayStatus}
    </Badge>
  );
};
